import { z } from 'zod';

/**
 * Response-side value that a newer server may extend. An unknown value parses
 * as `fallback` instead of failing the whole response, so an app already
 * installed on employee PCs keeps working against an updated company server.
 * Request bodies keep the strict schema.
 */
export function lenient<T extends z.ZodType>(schema: T, fallback: z.output<T>) {
  return schema.catch(fallback);
}

const lenientItems = new WeakMap<z.ZodType, z.ZodType>();

/**
 * List whose items a newer server may extend with new kinds (event streams).
 * Items that do not parse are dropped; the page cursor still advances.
 */
export function lenientArray<T extends z.ZodType>(item: T) {
  const list = z.array(z.unknown()).transform((items) =>
    items.flatMap((value) => {
      const parsed = item.safeParse(value);
      return parsed.success ? [parsed.data] : [];
    }),
  );
  lenientItems.set(list, item);
  return list;
}

/** The item schema of a `lenientArray` (the transform hides it from introspection). */
export function lenientItemOf(schema: z.ZodType): z.ZodType | undefined {
  return lenientItems.get(schema);
}
